import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import UserMenu from "../components/UserMenu";
import { ComponentTree } from "../components/ComponentRow";
import { Search, Plus, Upload, Trash2, Save, Package, Layers, ArrowLeft, FileSpreadsheet, Factory, Copy, Tag, Stethoscope, AlertTriangle, CheckCircle2, X, Container, Download, Link2 } from "lucide-react";
import { useStickyState } from "../safety/listPrefs";
import ResetFiltersChip from "../safety/ResetFiltersChip";
import SafetyShell from "../safety/SafetyShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function ProductCatalog({ user, onLogout }) {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState(null);
  const [tree, setTree] = useState([]);
  const [search, setSearch] = useStickyState("safety:catalog:search", "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pForm, setPForm] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showPalletImport, setShowPalletImport] = useState(false);
  const [palletImporting, setPalletImporting] = useState(false);
  const [palletImportResult, setPalletImportResult] = useState(null);
  const palletFileRef = useRef(null);
  const [showCatalogImport, setShowCatalogImport] = useState(false);
  const [catImporting, setCatImporting] = useState(false);
  const [catImportResult, setCatImportResult] = useState(null);
  const [catWipe, setCatWipe] = useState(true);
  const catFileRef = useRef(null);
  const [showAddComp, setShowAddComp] = useState(false);
  const [addParentId, setAddParentId] = useState(null);
  const [newComp, setNewComp] = useState({});
  const [canManage, setCanManage] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [dupCode, setDupCode] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [showAliasHealth, setShowAliasHealth] = useState(false);
  const [showMigration, setShowMigration] = useState(false);
  const [migPreview, setMigPreview] = useState(null);
  const [migLoading, setMigLoading] = useState(false);
  const [migApplying, setMigApplying] = useState(false);
  const [migConfirm, setMigConfirm] = useState(false);
  const [migBackups, setMigBackups] = useState([]);
  const [aliasHealth, setAliasHealth] = useState(null);
  const [aliasHealthLoading, setAliasHealthLoading] = useState(false);
  const [resolvingCode, setResolvingCode] = useState(null);
  const [aliasHealthCount, setAliasHealthCount] = useState(0);
  const fileRef = useRef(null);
  // brand-twins (products sharing the same physical pallet under different codes)
  const [twins, setTwins] = useState([]);
  const [twinSearch, setTwinSearch] = useState("");
  const [twinResults, setTwinResults] = useState([]);
  const [twinBusy, setTwinBusy] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/catalog/products${search ? `?search=${encodeURIComponent(search)}` : ""}`, authH());
      setProducts(r.data.products || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Permission denied");
    } finally { setLoading(false); }
  }, [search]);

  const fetchComps = useCallback(async (pid) => {
    try {
      const r = await axios.get(`${API}/catalog/products/${pid}/components`, authH());
      setTree(r.data.components || []);
    } catch { setTree([]); }
  }, []);

  const fetchTwins = useCallback(async (pid) => {
    try { const r = await axios.get(`${API}/catalog/products/${pid}/twins`, authH()); setTwins(r.data.twins || []); }
    catch { setTwins([]); }
  }, []);

  useEffect(() => { fetchProducts(); axios.get(`${API}/auth/me/permissions`, authH()).then(r => setCanManage((r.data.effective_permissions || []).includes("catalog.manage"))).catch(() => {}); }, [fetchProducts]);
  useEffect(() => { if (sel) { fetchComps(sel.id); setPForm({ ...sel }); fetchTwins(sel.id); setTwinSearch(""); setTwinResults([]); } }, [sel, fetchComps, fetchTwins]);

  useEffect(() => {
    if (!twinSearch.trim() || !sel) { setTwinResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/catalog/products?search=${encodeURIComponent(twinSearch)}`, authH());
        const have = new Set(twins.map(x => x.id));
        setTwinResults((r.data.products || []).filter(p => p.id !== sel.id && !have.has(p.id)).slice(0, 8));
      } catch { setTwinResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [twinSearch, sel, twins]);

  const linkTwin = async (pid) => {
    if (!sel) return;
    setTwinBusy(true);
    try {
      const r = await axios.post(`${API}/catalog/products/${sel.id}/twins`, { twin_ids: [pid] }, authH());
      setTwins(r.data.twins || []); setTwinSearch(""); setTwinResults([]);
      toast.success("Linked as brand-twin — container attributes shared");
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Link failed"); }
    finally { setTwinBusy(false); }
  };

  const unlinkTwin = async (pid) => {
    if (!sel) return;
    setTwinBusy(true);
    try {
      const r = await axios.post(`${API}/catalog/products/${sel.id}/twins/unlink`, { twin_id: pid }, authH());
      setTwins(r.data.twins || []);
      toast.success("Unlinked from brand-twins");
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Unlink failed"); }
    finally { setTwinBusy(false); }
  };

  const loadAliasHealth = async () => {
    setAliasHealthLoading(true);
    try {
      const r = await axios.get(`${API}/catalog/alias-health`, authH());
      setAliasHealth(r.data);
      setAliasHealthCount(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load alias health");
    } finally { setAliasHealthLoading(false); }
  };

  const fetchAliasHealthCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/catalog/alias-health`, authH());
      setAliasHealthCount(r.data.total || 0);
    } catch { /* no catalog.view or error — hide badge */ }
  }, []);

  const openAliasHealth = () => { setShowAliasHealth(true); loadAliasHealth(); };

  useEffect(() => {
    fetchAliasHealthCount();
    if (typeof window !== "undefined" && window.location.search.includes("aliasHealth=1")) {
      openAliasHealth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAliasHealthCount]);

  const addAliasFromHealth = async (issue) => {
    if (!issue.suggested_product_id) return;
    setResolvingCode(issue.code);
    try {
      await axios.post(`${API}/catalog/alias-health/add-alias`, {
        product_id: issue.suggested_product_id,
        alias: issue.code,
      }, authH());
      toast.success(`Linked "${issue.code}" → ${issue.suggested_product_code}`);
      await loadAliasHealth();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add alias");
    } finally { setResolvingCode(null); }
  };

  // ---- PO-code → primary catalog-code migration ----
  const loadMigration = async () => {
    setMigLoading(true);
    try {
      const [p, b] = await Promise.all([
        axios.get(`${API}/catalog/code-migration/preview`, authH()),
        axios.get(`${API}/catalog/code-migration/backups`, authH()),
      ]);
      setMigPreview(p.data);
      setMigBackups(b.data.backups || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load migration preview");
    } finally { setMigLoading(false); }
  };

  const openMigration = () => { setShowMigration(true); setMigConfirm(false); loadMigration(); };

  const applyMigration = async () => {
    setMigApplying(true);
    try {
      const r = await axios.post(`${API}/catalog/code-migration/apply`, {}, authH());
      toast.success(`Applied ${r.data.applied} code change(s). Revert available in this dialog.`);
      setMigConfirm(false);
      await loadMigration();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    } finally { setMigApplying(false); }
  };

  const revertMigration = async (backupId) => {
    if (!window.confirm("Revert this migration? All affected products will be restored to their pre-migration codes and aliases.")) return;
    setMigApplying(true);
    try {
      const r = await axios.post(`${API}/catalog/code-migration/revert`, { backup_id: backupId }, authH());
      toast.success(`Reverted — ${r.data.restored} product(s) restored`);
      await loadMigration();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revert failed");
    } finally { setMigApplying(false); }
  };


  const handleSaveProd = async () => {
    if (!pForm) return;
    setSaving(true);
    try {
      const payload = {
        ...pForm,
        aliases: typeof pForm.aliases === "string"
          ? pForm.aliases.split(",").map(s => s.trim()).filter(Boolean)
          : (Array.isArray(pForm.aliases) ? pForm.aliases : []),
      };
      await axios.put(`${API}/catalog/products/${pForm.id}`, payload, authH());
      toast.success("Saved");
      fetchProducts();
      const r = await axios.get(`${API}/catalog/products?search=${encodeURIComponent(pForm.product_code)}`, authH());
      const u = (r.data.products || []).find(p => p.id === pForm.id);
      if (u) setSel(u);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleCreateProd = async (d) => {
    try {
      const r = await axios.post(`${API}/catalog/products`, d, authH());
      toast.success(`'${r.data.product_code}' created`);
      setShowNew(false);
      fetchProducts();
      setSel(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelProd = async () => {
    if (!sel || !window.confirm(`Delete '${sel.product_code}' and all components?`)) return;
    try {
      await axios.delete(`${API}/catalog/products/${sel.id}`, authH());
      toast.success("Deleted");
      setSel(null); setTree([]);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDuplicate = () => {
    if (!sel) return;
    setDupCode(sel.product_code || "");
    setShowDuplicate(true);
  };

  const handleDuplicateProd = async () => {
    if (!sel) return;
    const code = (dupCode || "").trim();
    if (!code) { toast.error("New product code required"); return; }
    if (code === sel.product_code) { toast.error("New code must differ from the original"); return; }
    setDuplicating(true);
    try {
      const r = await axios.post(`${API}/catalog/products/${sel.id}/duplicate`, { new_product_code: code }, authH());
      toast.success(`Duplicated as '${r.data.product_code}' (${r.data.component_count} components)`);
      setShowDuplicate(false);
      setDupCode("");
      await fetchProducts();
      setSel(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Duplicate failed"); }
    finally { setDuplicating(false); }
  };

  const handleUpdComp = async (id, data) => {
    try {
      await axios.put(`${API}/catalog/components/${id}`, data, authH());
      toast.success("Updated");
      fetchComps(sel.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelComp = async (id) => {
    if (!window.confirm("Delete this component and children?")) return;
    try {
      await axios.delete(`${API}/catalog/components/${id}`, authH());
      toast.success("Deleted");
      fetchComps(sel.id); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAddComp = (parentId = null) => {
    setAddParentId(parentId);
    setNewComp({ sr_label: "", description: "", blank_size_l: "", blank_size_w: "", blank_size_t: "", material: "", qty_per_unit: "", remarks: "", is_assembly: false, model_number: "" });
    setShowAddComp(true);
  };

  const handleSaveNewComp = async () => {
    if (!newComp.description) { toast.error("Description required"); return; }
    try {
      await axios.post(`${API}/catalog/products/${sel.id}/components`, { ...newComp, parent_component_id: addParentId }, authH());
      toast.success("Added");
      setShowAddComp(false);
      fetchComps(sel.id); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/catalog/import-master-bom`, fd, { headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} new, updated ${r.data.updated} (${r.data.total_products} total)`);
      setShowImport(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleImportPalletDetails = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPalletImporting(true);
    setPalletImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/catalog/import-pallet-details`, fd, { headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      setPalletImportResult(r.data);
      toast.success(`Updated ${r.data.updated} product(s) from ${r.data.rows_parsed} rows`);
      fetchProducts();
      if (sel) { const rr = await axios.get(`${API}/catalog/products?search=${encodeURIComponent(sel.product_code)}`, authH()); const u = (rr.data.products || []).find(p => p.id === sel.id); if (u) setSel(u); }
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setPalletImporting(false); if (palletFileRef.current) palletFileRef.current.value = ""; }
  };

  const handleExportCatalog = async () => {
    try {
      const r = await axios.get(`${API}/catalog/export`, { ...authH(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalog-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Catalog exported");
    } catch { toast.error("Export failed"); }
  };

  const handleImportCatalog = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCatImporting(true);
    setCatImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/catalog/import-catalog?wipe=${catWipe}`, fd, { headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      setCatImportResult(r.data);
      toast.success(`Imported ${r.data.products_total} products + ${r.data.components_total} components`);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setCatImporting(false); if (catFileRef.current) catFileRef.current.value = ""; }
  };

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Product Catalog" }]}
      miniMap={[]}
    >
    <div className="bg-white border border-slate-200" data-testid="product-catalog-page">
      {/* Page header */}
      <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-orange-600" />
          <div><h1 className="text-lg font-semibold text-slate-900">Product Catalog</h1><p className="text-xs text-slate-500">Master BOM product definitions</p></div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openAliasHealth} data-testid="alias-health-btn" className="relative"><Stethoscope className="w-4 h-4 mr-1" />Alias Health{aliasHealthCount > 0 && (<span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="alias-health-count-badge">{aliasHealthCount}</span>)}</Button>
          {canManage && (<Button variant="outline" size="sm" onClick={openMigration} data-testid="code-migration-btn"><Tag className="w-4 h-4 mr-1" />Code Migration</Button>)}
          <Button variant="outline" size="sm" onClick={handleExportCatalog} data-testid="export-catalog-btn"><Download className="w-4 h-4 mr-1" />Export Catalog</Button>
          {canManage && (<><Button variant="outline" size="sm" onClick={() => { setShowCatalogImport(true); setCatImportResult(null); }} data-testid="import-catalog-btn"><Upload className="w-4 h-4 mr-1" />Import Catalog</Button><Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-master-bom-btn"><Upload className="w-4 h-4 mr-1" />Import Master BOM</Button><Button variant="outline" size="sm" onClick={() => { setShowPalletImport(true); setPalletImportResult(null); }} data-testid="import-pallet-details-btn"><Container className="w-4 h-4 mr-1" />Import Pallet Details</Button><Button size="sm" onClick={() => { setSel(null); setPForm(null); setShowNew(true); }} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" />Add Product</Button></>)}
        </div>
      </div>

      <div className="flex h-[calc(100vh-220px)] min-h-[480px]">
        {/* Left: Product List */}
        <div className="w-72 border-r border-slate-200 bg-white flex flex-col shrink-0">
          <div className="p-3 border-b border-slate-100">
            <div className="relative"><Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" /><Input className="pl-8 h-8 text-sm" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search-input" /></div>
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-[10px] text-slate-400">{products.length} products</p>
              <ResetFiltersChip active={search !== ""} onReset={() => setSearch("")} label="Clear" />
            </div>
          </div>
          <ScrollArea className="flex-1">
            <TooltipProvider delayDuration={150}>
            {products.map(p => {
              const aliases = Array.isArray(p.aliases) ? p.aliases.filter(Boolean) : [];
              return (
              <div key={p.id} className={`relative group border-b border-slate-50 hover:bg-slate-50 transition-colors ${sel?.id === p.id ? "bg-emerald-50 border-l-2 border-l-emerald-500" : ""}`}>
                <button onClick={() => { setSel(p); setShowNew(false); }} className="w-full text-left px-3 py-2 pr-9" data-testid={`product-item-${p.product_code}`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-sm font-medium text-slate-800 truncate">{p.product_code}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {aliases.length > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center gap-0.5 h-4 px-1 rounded text-[9px] bg-sky-100 text-sky-700 border border-sky-200 cursor-help"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`product-alias-badge-${p.product_code}`}
                            >
                              <Tag className="w-2.5 h-2.5" />
                              {aliases.length}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <div className="font-medium mb-0.5">Aliases:</div>
                            <div className="font-mono text-[11px] leading-relaxed">
                              {aliases.join(", ")}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {p.inhouse_only && <Badge className="text-[9px] h-4 px-1 bg-amber-100 text-amber-700 border-amber-200">Inhouse</Badge>}
                      <Badge variant="secondary" className="text-[10px] h-4 px-1">{p.component_count}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{p.description}</p>
                </button>
                {canManage && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSel(p); setDupCode(p.product_code || ""); setShowDuplicate(true); }}
                    title={`Duplicate ${p.product_code}`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-emerald-100 text-slate-400 hover:text-emerald-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    data-testid={`duplicate-product-row-${p.product_code}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              );
            })}
            </TooltipProvider>
            {products.length === 0 && !loading && <div className="p-6 text-center text-sm text-slate-400">{search ? "No match" : "No products. Import a Master BOM."}</div>}
          </ScrollArea>
        </div>

        {/* Right: Detail */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          {sel && pForm ? (<>
            {/* Product fields */}
            <div className="bg-white border-b border-slate-200 p-4">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2"><label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Description</label><Input className="h-8 text-sm mt-0.5" value={pForm.description || ""} onChange={e => setPForm({...pForm, description: e.target.value})} disabled={!canManage} data-testid="product-description-input" /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Product Code</label><Input className="h-8 text-sm mt-0.5 font-mono" value={pForm.product_code || ""} onChange={e => setPForm({...pForm, product_code: e.target.value})} disabled={!canManage} data-testid="product-code-input" /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Rev No</label><Input className="h-8 text-sm mt-0.5" value={pForm.rev_no || ""} onChange={e => setPForm({...pForm, rev_no: e.target.value})} disabled={!canManage} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Colour</label><Input className="h-8 text-sm mt-0.5" value={pForm.colour || ""} onChange={e => setPForm({...pForm, colour: e.target.value})} disabled={!canManage} /></div>
                <div><label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Remarks</label><Input className="h-8 text-sm mt-0.5" value={pForm.remarks || ""} onChange={e => setPForm({...pForm, remarks: e.target.value})} disabled={!canManage} /></div>
              </div>
              {/* Aliases — alternate codes that resolve to this product (legacy POs, shortform scans) */}
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1">
                  Aliases
                  <span className="text-slate-400 normal-case font-normal lowercase tracking-normal">
                    — alternate codes that resolve to this product (type + Enter or comma to add)
                  </span>
                </label>
                <AliasTagInput
                  value={pForm.aliases}
                  onChange={(next) => setPForm({ ...pForm, aliases: next })}
                  disabled={!canManage}
                  data-testid="product-aliases-input"
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">Weight Mode:</label>
                    <select className="text-xs border rounded px-2 py-1 bg-white" value={pForm.weight_mode || "auto"} onChange={e => setPForm({...pForm, weight_mode: e.target.value})} disabled={!canManage} data-testid="weight-mode-select">
                      <option value="auto">Auto (from components)</option>
                      <option value="manual">Manual (enter weight)</option>
                    </select>
                  </div>
                  {pForm.weight_mode === "manual" && (
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-500">Wt/pc (kg):</label>
                      <Input className="h-7 w-24 text-xs" type="number" step="0.01" value={pForm.weight_per_piece || ""} onChange={e => setPForm({...pForm, weight_per_piece: e.target.value})} disabled={!canManage} data-testid="manual-weight-input" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 ml-4 border-l pl-4 border-slate-200">
                    <Factory className="w-3.5 h-3.5 text-amber-500" />
                    <label className="text-xs text-slate-500">Inhouse Only:</label>
                    <input type="checkbox" checked={!!pForm.inhouse_only} onChange={e => setPForm({...pForm, inhouse_only: e.target.checked})} disabled={!canManage} className="rounded border-slate-300 text-amber-500 focus:ring-amber-400 h-4 w-4" data-testid="inhouse-only-checkbox" />
                  </div>
                  <div className="flex items-center gap-1 ml-4 border-l pl-4 border-slate-200">
                    <label className="text-xs text-slate-500">Qty/Pallet:</label>
                    <Input className="h-7 w-20 text-xs" type="number" min="1" step="1" value={pForm.default_qty_per_pallet || ""} onChange={e => setPForm({...pForm, default_qty_per_pallet: e.target.value})} disabled={!canManage} data-testid="qty-per-pallet-input" placeholder="—" />
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={openDuplicate} data-testid="duplicate-product-btn"><Copy className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
                    <Button size="sm" variant="destructive" onClick={handleDelProd} data-testid="delete-product-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                    <Button size="sm" onClick={handleSaveProd} disabled={saving} data-testid="save-product-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? "Saving..." : "Save Product"}</Button>
                  </div>
                )}
              </div>
              {/* Container-planning physical attributes */}
              <div className="mt-3 pt-3 border-t border-slate-100" data-testid="physical-attrs-section">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5 flex items-center gap-1"><Container className="w-3.5 h-3.5 text-blue-600" />Container-planning attributes (inches / kg)</div>
                <div className="grid grid-cols-4 lg:grid-cols-8 gap-2 items-end">
                  <div><label className="text-[10px] text-slate-500">Pallet L (in)</label><Input className="h-7 text-xs" type="number" step="0.1" value={pForm.pallet_length_in ?? ""} onChange={e => setPForm({...pForm, pallet_length_in: e.target.value})} disabled={!canManage} data-testid="pallet-length-input" /></div>
                  <div><label className="text-[10px] text-slate-500">Pallet W (in)</label><Input className="h-7 text-xs" type="number" step="0.1" value={pForm.pallet_width_in ?? ""} onChange={e => setPForm({...pForm, pallet_width_in: e.target.value})} disabled={!canManage} data-testid="pallet-width-input" /></div>
                  <div><label className="text-[10px] text-slate-500">Pallet H (in)</label><Input className="h-7 text-xs" type="number" step="0.1" value={pForm.pallet_height_in ?? ""} onChange={e => setPForm({...pForm, pallet_height_in: e.target.value})} disabled={!canManage} data-testid="pallet-height-input" /></div>
                  <div><label className="text-[10px] text-slate-500">Finished wt/pc (kg)</label><Input className="h-7 text-xs" type="number" step="0.01" value={pForm.actual_finished_weight_kg ?? ""} onChange={e => setPForm({...pForm, actual_finished_weight_kg: e.target.value})} disabled={!canManage} data-testid="actual-weight-input" /></div>
                  <div><label className="text-[10px] text-slate-500">Pallet tare (kg)</label><Input className="h-7 text-xs" type="number" step="0.1" value={pForm.pallet_tare_weight_kg ?? ""} onChange={e => setPForm({...pForm, pallet_tare_weight_kg: e.target.value})} disabled={!canManage} data-testid="pallet-tare-input" /></div>
                  <div><label className="text-[10px] text-slate-500">Max stack layers</label><Input className="h-7 text-xs" type="number" min="1" step="1" value={pForm.max_stack_layers ?? ""} onChange={e => setPForm({...pForm, max_stack_layers: e.target.value})} disabled={!canManage} data-testid="max-layers-input" placeholder="1" /></div>
                  <div><label className="text-[10px] text-slate-500">Load priority</label><Input className="h-7 text-xs" type="number" step="1" value={pForm.load_priority ?? ""} onChange={e => setPForm({...pForm, load_priority: e.target.value})} disabled={!canManage} data-testid="load-priority-input" placeholder="0" /></div>
                  <div className="flex items-center gap-1.5 pb-1"><input type="checkbox" checked={pForm.stackable !== false} onChange={e => setPForm({...pForm, stackable: e.target.checked})} disabled={!canManage} className="h-4 w-4 rounded border-slate-300" data-testid="stackable-checkbox" /><label className="text-[11px] text-slate-500">Stackable</label></div>
                  <div className="flex items-center gap-1.5 pb-1"><input type="checkbox" checked={pForm.rotatable === true} onChange={e => setPForm({...pForm, rotatable: e.target.checked})} disabled={!canManage} className="h-4 w-4 rounded border-slate-300" data-testid="rotatable-checkbox" /><label className="text-[11px] text-slate-500">Rotatable</label></div>
                  <div className="flex items-center gap-1.5 pb-1"><input type="checkbox" checked={pForm.stack_weight_exempt === true} onChange={e => setPForm({...pForm, stack_weight_exempt: e.target.checked})} disabled={!canManage} className="h-4 w-4 rounded border-slate-300" data-testid="stack-weight-exempt-checkbox" /><label className="text-[11px] text-slate-500 leading-tight">Exempt stack wt limit</label></div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Finished wt/pc is the real measured weight (used for container planning); it overrides the BOM-derived sum. <b>Rotatable</b> lets the planner turn this pallet 90° (off by default). <b>Exempt stack wt limit</b> lets its stacks exceed the per-stack weight cap.</p>
                {pForm.id && (
                  <div className="mt-3 pt-3 border-t border-slate-100" data-testid="brand-twins-box">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5 flex items-center gap-1"><Link2 className="w-3.5 h-3.5 text-violet-600" />Brand twins <span className="text-slate-300 normal-case tracking-normal">· same physical pallet under different codes</span></div>
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="brand-twins-list">
                      {twins.length === 0 && <span className="text-[11px] text-slate-400">No twins linked.</span>}
                      {twins.map(t => (
                        <span key={t.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs font-mono" data-testid={`brand-twin-chip-${t.product_code}`}>
                          {t.product_code}
                          {canManage && <button onClick={() => unlinkTwin(t.id)} disabled={twinBusy} className="hover:bg-violet-200 rounded-full p-0.5" data-testid={`brand-twin-unlink-${t.product_code}`}><X className="w-3 h-3" /></button>}
                        </span>
                      ))}
                    </div>
                    {canManage && (
                      <div className="relative">
                        <Input className="h-7 text-xs" placeholder="Search a product code to link as a twin…" value={twinSearch} onChange={e => setTwinSearch(e.target.value)} data-testid="brand-twin-search" />
                        {twinResults.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto" data-testid="brand-twin-results">
                            {twinResults.map(p => (
                              <button key={p.id} onClick={() => linkTwin(p.id)} disabled={twinBusy} className="w-full text-left px-2.5 py-1.5 hover:bg-violet-50 text-xs border-b border-slate-50 last:border-0" data-testid={`brand-twin-option-${p.product_code}`}>
                                <span className="font-mono font-medium">{p.product_code}</span> <span className="text-slate-400">{p.description}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {twins.length > 0 && <p className="text-[10px] text-slate-400 mt-1">Editing the container attributes above and saving pushes them to all {twins.length + 1} linked products.</p>}
                  </div>
                )}
              </div>
            </div>
            {/* Components */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
                <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-slate-600" /><span className="text-sm font-medium text-slate-700">Components</span><Badge variant="secondary" className="text-[10px]">{tree.length} top-level</Badge></div>
                {canManage && <Button size="sm" variant="outline" onClick={() => handleAddComp(null)} data-testid="add-top-component-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add Component</Button>}
              </div>
              <div className="grid grid-cols-[20px_20px_40px_1fr_110px_50px_50px_60px_80px] items-center gap-1 px-4 py-1 bg-slate-50 border-b text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                <span></span><span></span><span>Sr</span><span>Description</span><span className="text-right">Dimensions</span><span>Mat</span><span className="text-right">Qty</span><span className="text-right">Wt</span><span></span>
              </div>
              <div>
                {tree.length > 0 ? <ComponentTree tree={tree} onUpdate={handleUpdComp} onDelete={handleDelComp} onAddChild={handleAddComp} canManage={canManage} /> : <div className="p-8 text-center text-sm text-slate-400">No components. Click "Add Component" to start.</div>}
              </div>
            </div>
          </>) : showNew ? (
            <NewProductForm onSave={handleCreateProd} onCancel={() => setShowNew(false)} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400"><div className="text-center"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="text-sm">Select a product to view details</p></div></div>
          )}
        </div>
      </div>

      {/* Duplicate Product Dialog */}
      <Dialog open={showDuplicate} onOpenChange={(o) => { if (!duplicating) setShowDuplicate(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-emerald-600" />Duplicate Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {sel && (
              <p className="text-sm text-slate-600">
                Copying <span className="font-mono font-semibold">{sel.product_code}</span>
                {sel.component_count ? <> with <span className="font-semibold">{sel.component_count}</span> component(s)</> : null}.
                Aliases will <span className="font-semibold">not</span> be copied. Edit the new code below, then continue.
              </p>
            )}
            <div>
              <label className="text-xs text-slate-500">New Product Code *</label>
              <Input
                className="h-9 text-sm font-mono mt-0.5"
                value={dupCode}
                onChange={(e) => setDupCode(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && !duplicating) handleDuplicateProd(); }}
                data-testid="duplicate-new-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicate(false)} disabled={duplicating}>Cancel</Button>
            <Button
              onClick={handleDuplicateProd}
              disabled={duplicating || !dupCode.trim() || dupCode.trim() === sel?.product_code}
              data-testid="duplicate-confirm-btn"
            >
              {duplicating ? "Duplicating..." : "Duplicate & Edit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alias Health Dialog */}
      <Dialog open={showAliasHealth} onOpenChange={setShowAliasHealth}>
        <DialogContent className="max-w-3xl" data-testid="alias-health-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-5 h-5 text-orange-600" />Alias Health</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Product codes used in POs, production reports, pallet scans or packing lists that don't cleanly map to a catalog product.
            Linking them as aliases ensures produced &amp; dispatched quantities reconcile correctly.
          </p>
          {aliasHealthLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" /></div>
          ) : !aliasHealth ? null : aliasHealth.total === 0 ? (
            <div className="py-10 text-center" data-testid="alias-health-clean">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium text-slate-700">All clear — every referenced code maps to the catalog.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs">
                <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="alias-health-suggested-count"><b>{aliasHealth.counts.suggested}</b> with a suggested match</span>
                {aliasHealth.counts.unmatched > 0 && <span className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200"><b>{aliasHealth.counts.unmatched}</b> unmatched (no catalog product)</span>}
              </div>
              <ScrollArea className="max-h-[55vh] mt-2 border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Used Code</th>
                      <th className="text-left p-2">Where</th>
                      <th className="text-left p-2">Suggested Product</th>
                      <th className="text-right p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aliasHealth.issues.map((it) => (
                      <tr key={it.code} className="border-t hover:bg-slate-50/60" data-testid={`alias-health-row-${it.code}`}>
                        <td className="p-2">
                          <span className="font-mono font-medium">{it.code}</span>
                          <span className="ml-1 text-[10px] text-slate-400">×{it.count}</span>
                        </td>
                        <td className="p-2 text-xs text-slate-500">{it.sources.join(", ")}</td>
                        <td className="p-2">
                          {it.suggested_product_code ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{it.suggested_product_code}</span>
                              <span className="text-[11px] text-slate-400 truncate max-w-[180px]" title={it.suggested_product_description}>{it.suggested_product_description}</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5" />No match — create a product</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {it.suggested_product_id && canManage && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={resolvingCode === it.code} onClick={() => addAliasFromHealth(it)} data-testid={`alias-health-link-${it.code}`}>
                              {resolvingCode === it.code ? "Linking…" : (<><Tag className="w-3 h-3 mr-1" />Link as alias</>)}
                            </Button>
                          )}
                          {!canManage && it.suggested_product_id && <span className="text-[11px] text-slate-400">view only</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={loadAliasHealth} disabled={aliasHealthLoading} data-testid="alias-health-refresh">Refresh</Button>
            <Button onClick={() => setShowAliasHealth(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO-code → primary catalog-code migration */}
      <Dialog open={showMigration} onOpenChange={setShowMigration}>
        <DialogContent className="max-w-4xl" data-testid="code-migration-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-indigo-600" />PO-code → Primary code migration</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            For PO product codes that resolve to a catalog item but aren't its primary code, the <b>PO code becomes the primary code</b> and the old primary code is kept as an <b>alias</b>. A full before-image snapshot is saved so you can revert with one click.
          </p>

          {migLoading && <div className="py-10 text-center text-slate-500" data-testid="mig-loading">Loading preview…</div>}

          {!migLoading && migPreview && (
            <>
              <div className="flex flex-wrap gap-2 text-xs mb-1">
                <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="mig-change-count"><b>{migPreview.summary.to_change}</b> to change</span>
                <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="mig-conflict-count"><b>{migPreview.summary.conflicts}</b> conflicts (skipped)</span>
                <span className="px-2 py-1 rounded bg-slate-50 text-slate-600 border border-slate-200" data-testid="mig-nomatch-count"><b>{migPreview.summary.no_match}</b> no catalog match</span>
              </div>

              {migPreview.changes.length > 0 && (
                <ScrollArea className="h-[280px] border rounded-md">
                  <table className="w-full text-xs" data-testid="mig-changes-table">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr className="text-left"><th className="px-2 py-1.5">Old primary</th><th className="px-2 py-1.5"></th><th className="px-2 py-1.5">New primary (PO code)</th><th className="px-2 py-1.5">Description</th><th className="px-2 py-1.5 text-right">PO refs</th><th className="px-2 py-1.5">Aliases after</th></tr>
                    </thead>
                    <tbody>
                      {migPreview.changes.map((c) => (
                        <tr key={c.product_id} className="border-t hover:bg-slate-50/60" data-testid={`mig-row-${c.new_code}`}>
                          <td className="px-2 py-1.5 font-mono text-slate-500 line-through">{c.old_code}</td>
                          <td className="px-2 py-1.5 text-indigo-500">→</td>
                          <td className="px-2 py-1.5 font-mono font-semibold text-indigo-700">{c.new_code}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[220px]">{c.description}</td>
                          <td className="px-2 py-1.5 text-right text-slate-500">{c.po_count}</td>
                          <td className="px-2 py-1.5 text-slate-400">{(c.resulting_aliases || []).join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}

              {migPreview.conflicts.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="font-semibold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Conflicts (not changed — resolve manually)</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-slate-600 max-h-24 overflow-auto">
                    {migPreview.conflicts.map((c, i) => (<li key={i}><span className="font-mono">{c.old_code}</span> — {c.reason}</li>))}
                  </ul>
                </div>
              )}

              {migBackups.length > 0 && (
                <div className="mt-3 border-t pt-2">
                  <div className="text-xs font-semibold text-slate-700 mb-1">Migration history / revert</div>
                  <div className="space-y-1 max-h-28 overflow-auto" data-testid="mig-backups">
                    {migBackups.map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                        <span className="text-slate-600">{new Date(b.created_at).toLocaleString()} · {b.change_count} change(s) · {b.created_by}{b.reverted_at ? " · reverted" : ""}</span>
                        <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={!!b.reverted_at || migApplying} onClick={() => revertMigration(b.id)} data-testid={`mig-revert-${b.id}`}>{b.reverted_at ? "Reverted" : "Revert"}</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={loadMigration} disabled={migLoading || migApplying} data-testid="mig-refresh">Refresh</Button>
            {migConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600">Apply {migPreview?.summary.to_change} change(s) to the live catalog?</span>
                <Button size="sm" variant="destructive" onClick={applyMigration} disabled={migApplying} data-testid="mig-apply-confirm">{migApplying ? "Applying…" : "Yes, apply"}</Button>
                <Button size="sm" variant="outline" onClick={() => setMigConfirm(false)} disabled={migApplying}>Cancel</Button>
              </div>
            ) : (
              <Button onClick={() => setMigConfirm(true)} disabled={migApplying || !migPreview || migPreview.summary.to_change === 0} data-testid="mig-apply-btn">Apply migration ({migPreview?.summary.to_change || 0})</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent><DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" />Import Master BOM</DialogTitle></DialogHeader>
          <div className="py-4"><p className="text-sm text-slate-600 mb-3">Upload Master BOM Excel (.xlsx). Existing products will be updated.</p><input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="text-sm" disabled={importing} data-testid="import-file-input" />{importing && <p className="text-sm text-blue-600 mt-2">Importing...</p>}</div>
        </DialogContent>
      </Dialog>

      {/* Import Pallet Details Dialog */}
      <Dialog open={showPalletImport} onOpenChange={setShowPalletImport}>
        <DialogContent className="max-w-lg" data-testid="pallet-import-dialog"><DialogHeader><DialogTitle className="flex items-center gap-2"><Container className="w-5 h-5 text-blue-700" />Import Pallet Details</DialogTitle></DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-600 mb-3">Upload the <b>Master Pallet Details</b> Excel (.xlsx). It fills each product's pallet size (L×W), height, finished weight/pc, pallet tare and pieces/pallet — matched by model code.</p>
            <input ref={palletFileRef} type="file" accept=".xlsx,.xlsm" onChange={handleImportPalletDetails} className="text-sm" disabled={palletImporting} data-testid="pallet-import-file-input" />
            {palletImporting && <p className="text-sm text-blue-600 mt-2">Importing...</p>}
            {palletImportResult && (
              <div className="mt-3 text-sm space-y-2" data-testid="pallet-import-result">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{palletImportResult.updated} updated</Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200">{palletImportResult.rows_parsed} rows</Badge>
                  {palletImportResult.unmatched_count > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200">{palletImportResult.unmatched_count} unmatched</Badge>}
                </div>
                {palletImportResult.missing_weight?.length > 0 && <div className="text-xs text-amber-700"><b>Still missing finished weight:</b> {palletImportResult.missing_weight.join(", ")}</div>}
                {palletImportResult.missing_dims?.length > 0 && <div className="text-xs text-amber-700"><b>Still missing dimensions:</b> {palletImportResult.missing_dims.join(", ")}</div>}
                {palletImportResult.unmatched?.length > 0 && <div className="text-xs text-slate-500"><b>Codes not in catalog:</b> {palletImportResult.unmatched.slice(0, 20).join(", ")}{palletImportResult.unmatched.length > 20 ? " …" : ""}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Catalog (production→preview sync) Dialog */}
      <Dialog open={showCatalogImport} onOpenChange={setShowCatalogImport}>
        <DialogContent className="max-w-lg" data-testid="catalog-import-dialog"><DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-indigo-600" />Import Catalog (sync)</DialogTitle></DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-600 mb-2">Upload a <b>catalog-export .json</b> file (from the <b>Export Catalog</b> button on another environment, e.g. production). This copies all products + BOM components here so you can test POs without publishing.</p>
            <label className="flex items-center gap-2 text-sm text-slate-600 mb-2">
              <input type="checkbox" checked={catWipe} onChange={e => setCatWipe(e.target.checked)} className="h-4 w-4 rounded border-slate-300" data-testid="catalog-wipe-checkbox" />
              Replace everything (wipe existing catalog first for an exact mirror)
            </label>
            <input ref={catFileRef} type="file" accept=".json" onChange={handleImportCatalog} className="text-sm" disabled={catImporting} data-testid="catalog-import-file-input" />
            {catImporting && <p className="text-sm text-blue-600 mt-2">Importing…</p>}
            {catImportResult && (
              <div className="mt-3 text-sm space-y-2" data-testid="catalog-import-result">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{catImportResult.products_total} products</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{catImportResult.components_total} components</Badge>
                  {catImportResult.wiped && <Badge className="bg-rose-100 text-rose-700 border-rose-200">wiped first</Badge>}
                </div>
                <div className="text-xs text-slate-500">Created {catImportResult.products_created}, updated {catImportResult.products_updated}.</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Add Component Dialog */}
      <Dialog open={showAddComp} onOpenChange={setShowAddComp}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{addParentId ? "Add Sub-Component" : "Add Component"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Sr Label</label><Input className="h-8 text-sm" placeholder="e.g. 1, 1.1" value={newComp.sr_label || ""} onChange={e => setNewComp({...newComp, sr_label: e.target.value})} data-testid="new-comp-sr-label" /></div>
              <div><label className="text-xs text-slate-500">Model Number</label><Input className="h-8 text-sm" placeholder="Optional" value={newComp.model_number || ""} onChange={e => setNewComp({...newComp, model_number: e.target.value})} /></div>
            </div>
            <div><label className="text-xs text-slate-500">Description *</label><Input className="h-8 text-sm" placeholder="Component description" value={newComp.description || ""} onChange={e => setNewComp({...newComp, description: e.target.value})} data-testid="new-comp-description" /></div>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="text-xs text-slate-500">L (mm)</label><Input className="h-8 text-sm" type="number" value={newComp.blank_size_l || ""} onChange={e => setNewComp({...newComp, blank_size_l: e.target.value})} data-testid="new-comp-l" /></div>
              <div><label className="text-xs text-slate-500">W (mm)</label><Input className="h-8 text-sm" type="number" value={newComp.blank_size_w || ""} onChange={e => setNewComp({...newComp, blank_size_w: e.target.value})} /></div>
              <div><label className="text-xs text-slate-500">T (mm)</label><Input className="h-8 text-sm" type="number" value={newComp.blank_size_t || ""} onChange={e => setNewComp({...newComp, blank_size_t: e.target.value})} /></div>
              <div><label className="text-xs text-slate-500">Material</label><Input className="h-8 text-sm" placeholder="HR, CR" value={newComp.material || ""} onChange={e => setNewComp({...newComp, material: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs text-slate-500">Qty per Unit</label><Input className="h-8 text-sm" type="number" value={newComp.qty_per_unit || ""} onChange={e => setNewComp({...newComp, qty_per_unit: e.target.value})} data-testid="new-comp-qty" /></div>
              <div><label className="text-xs text-slate-500">Remarks</label><Input className="h-8 text-sm" value={newComp.remarks || ""} onChange={e => setNewComp({...newComp, remarks: e.target.value})} /></div>
              <div className="flex items-end pb-1"><label className="text-xs text-slate-500 flex items-center gap-1.5"><input type="checkbox" checked={newComp.is_assembly || false} onChange={e => setNewComp({...newComp, is_assembly: e.target.checked})} className="w-4 h-4" />Assembly</label></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddComp(false)}>Cancel</Button><Button onClick={handleSaveNewComp} data-testid="save-new-component-btn">Add Component</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}

function AliasTagInput({ value, onChange, disabled, "data-testid": testId }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value)
    ? value.filter(Boolean)
    : (value ? String(value).split(",").map(s => s.trim()).filter(Boolean) : []);

  const commit = (raw) => {
    const parts = String(raw).split(",").map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const existing = new Set(tags.map(t => t.toLowerCase()));
    const next = [...tags];
    for (const p of parts) {
      if (!existing.has(p.toLowerCase())) { next.push(p); existing.add(p.toLowerCase()); }
    }
    onChange(next);
    setDraft("");
  };

  const removeAt = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && tags.length) {
      removeAt(tags.length - 1);
    }
  };

  return (
    <div
      className={`mt-0.5 min-h-8 w-full rounded-md border border-input bg-background px-2 py-1 flex flex-wrap items-center gap-1 ${disabled ? "opacity-60" : "focus-within:ring-1 focus-within:ring-ring"}`}
      data-testid={testId ? `${testId}-tags` : "alias-tags"}
      onClick={(e) => { if (!disabled) e.currentTarget.querySelector("input")?.focus(); }}
    >
      {tags.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center gap-1 rounded bg-slate-100 text-slate-700 text-xs font-mono px-1.5 py-0.5 border border-slate-200"
          data-testid={`alias-pill-${t}`}
        >
          {t}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeAt(i); }}
              className="text-slate-400 hover:text-rose-500"
              data-testid={`alias-pill-remove-${t}`}
              aria-label={`Remove ${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          className="flex-1 min-w-[80px] bg-transparent outline-none text-sm font-mono py-0.5"
          placeholder={tags.length ? "" : "GR-8, GR-8-HT"}
          value={draft}
          onChange={(e) => {
            const v = e.target.value;
            if (v.includes(",")) commit(v); else setDraft(v);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          data-testid={testId || "product-aliases-input"}
        />
      )}
    </div>
  );
}

function NewProductForm({ onSave, onCancel }) {
  const [f, setF] = useState({ product_code: "", description: "", rev_no: "", colour: "", remarks: "", weight_mode: "auto", weight_per_piece: "" });
  return (
    <div className="flex-1 p-8">
      <div className="max-w-lg mx-auto bg-white rounded-lg border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">New Product</h2>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-500">Product Code *</label><Input className="h-9 text-sm" placeholder="e.g. P12-6" value={f.product_code} onChange={e => setF({...f, product_code: e.target.value})} data-testid="new-product-code" /></div>
          <div><label className="text-xs text-slate-500">Description *</label><Input className="h-9 text-sm" placeholder="Product description" value={f.description} onChange={e => setF({...f, description: e.target.value})} data-testid="new-product-description" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-500">Rev No</label><Input className="h-9 text-sm" value={f.rev_no} onChange={e => setF({...f, rev_no: e.target.value})} /></div>
            <div><label className="text-xs text-slate-500">Colour</label><Input className="h-9 text-sm" value={f.colour} onChange={e => setF({...f, colour: e.target.value})} /></div>
            <div><label className="text-xs text-slate-500">Remarks</label><Input className="h-9 text-sm" value={f.remarks} onChange={e => setF({...f, remarks: e.target.value})} /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(f)} disabled={!f.product_code || !f.description} data-testid="create-product-btn">Create Product</Button>
        </div>
      </div>
    </div>
  );
}
